import { useMemo, useState } from 'react';
import type { ProjectLayoutData, ProjectLayoutMode } from '../types';
import { capabilitiesForMode } from '../types';
import { Button } from '@/components/ui/button';
import { ClauseRow, splitHtmlIntoClauses } from './SectionIV_ContractTerms';
import { SectionHeading } from './SectionHeading';

/**
 * Sprint 41 §14.S10 — Section V "Warranty".
 *
 * Renders Document Language / SectionTemplate LIMITED_LIABILITY_WARRANTY
 * body, or `Project.limitedLiabilityWarrantyOverride` when the estimate
 * stamped (or accepted) a snapshot of company language.
 *
 * Founder mockup 2026-07-16 — same collapsed-clauses idiom as Section
 * IV (workmanship / materials / exclusions rows when the company's
 * warranty HTML uses headings; a single expandable row otherwise).
 * The data model has no structured warranty entity, so the rows are a
 * display-only split of the company warranty HTML. The section renders
 * ONLY when warranty content exists.
 *
 * Visibility: per `capabilities.showWarranty`. Per §14.S12-Q3 lock,
 * this section is OMITTED in proposal mode. Estimate / review /
 * contract modes render when content exists.
 */
export interface SectionV_WarrantyProps {
  data: ProjectLayoutData;
  mode: ProjectLayoutMode;
}

export function SectionV_Warranty({ data, mode }: SectionV_WarrantyProps) {
  const caps = capabilitiesForMode(mode);

  const sectionTemplates =
    (data.sectionTemplates as Array<{
      sectionCode: string;
      bodyHandlebars: string;
    }> | undefined) ?? [];
  const template = sectionTemplates.find(
    (t) => t.sectionCode === 'LIMITED_LIABILITY_WARRANTY',
  );
  const override =
    (data.project.limitedLiabilityWarrantyOverride as string | undefined | null) ??
    null;
  const body = (override ?? template?.bodyHandlebars ?? '').trim();

  const clauses = useMemo(
    () => (body ? splitHtmlIntoClauses(body, 'Limited Warranty') : []),
    [body],
  );
  const [openClauses, setOpenClauses] = useState<ReadonlySet<number>>(new Set());
  const toggleClause = (i: number) =>
    setOpenClauses((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  const allOpen = clauses.length > 0 && openClauses.size === clauses.length;
  const toggleAll = () =>
    setOpenClauses(allOpen ? new Set() : new Set(clauses.map((_, i) => i)));

  if (!caps.showWarranty) return null;
  // Founder mockup 2026-07-16 — render the section only when the
  // company actually has warranty content.
  if (!body) return null;

  return (
    <section
      id="section-5"
      className="scroll-mt-24"
      data-mode={mode}
      data-section="V"
    >
      <div className="rounded-xl border border-border bg-card shadow-sm">
        <SectionHeading numeral="V." title="Limited Liability Warranty" className="px-6 pt-5 pb-1" />
        <p className="px-6 pb-4 font-public-sans text-xs text-muted-foreground">
          Coverage details listed in order. Each row is collapsed by default; tap a
          row to expand.
        </p>

        <div className="px-6 pb-6">
          <div className="divide-y divide-border rounded-md border border-border">
            {clauses.map((clause, i) => (
              <ClauseRow
                key={i}
                ordinal={`§ 5.${i + 1}`}
                title={clause.title}
                html={clause.html}
                open={openClauses.has(i)}
                onToggle={() => toggleClause(i)}
              />
            ))}
          </div>
          {clauses.length > 1 && (
            <div className="mt-3 flex justify-end">
              <Button type="button" variant="ghost" size="sm"
                onClick={toggleAll}
                className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim"
              >
                {allOpen ? 'Collapse all ▴' : 'Expand all ▾'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
